import { Expose, Type } from 'class-transformer';
import { GeneralOverviewResponseDto } from './general-overview.response.dto';
import { LicensableActivityResponseDto } from './licensable-activity.response.dto';
import { RegulatoryDetailsResponseDto } from './regulatory-details/regulatory-details.response.dto';
import { ServicesProvidedType, StorageType, DocumentControlType } from '../../../enums';

export class ProductStorageResponseDto {
    @Expose()
    id: string;

    @Expose()
    clientInformationRecordId: string;

    @Expose()
    storageType: StorageType;

    @Expose()
    isSelected: boolean;

    @Expose()
    @Type(() => Date)
    createdAt: Date;

    @Expose()
    @Type(() => Date)
    updatedAt: Date;
}

export class DocumentApprovalResponseDto {
    @Expose()
    id: string;

    @Expose()
    clientInformationRecordId: string;

    @Expose()
    signedBy?: string;

    @Expose()
    signerName?: string;

    @Expose()
    signingReason?: string;

    @Expose()
    @Type(() => Date)
    signingTime?: Date;

    @Expose()
    signatureHash?: string;

    @Expose()
    @Type(() => Date)
    createdAt: Date;

    @Expose()
    @Type(() => Date)
    updatedAt: Date;
}

export class ClientInformationRecordResponseDto {
    /**
     * Unique identifier for the client information record
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @Expose()
    id: string;

    /**
     * Document identifier for the client information record
     * @example 'QR-043-5'
     */
    @Expose()
    documentNumber: string;

    /**
     * Name of the client organization
     * @example 'ACME Pharmaceuticals Canada Inc.'
     */
    @Expose()
    clientName: string;

    /**
     * Physical address of the client organization
     * @example '123 Business Parkway, Suite 400, Toronto'
     */
    @Expose()
    clientAddress: string;

    /**
     * Website URL of the client organization
     * @example 'www.acmepharma.com/canada/'
     */
    @Expose()
    clientWebsite: string;

    @Expose()
    mahHolder?: boolean;

    /**
     * Effective date of this client information record
     * @example '2024-01-15T00:00:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    asOfDate: Date;

    @Expose()
    @Type(() => Date)
    deletedAt?: Date | null;

    /**
     * Type of service provided by Q&C Services to this client
     * @example 'QCO_SERVICES'
     */
    @Expose()
    servicesProvidedByQc: ServicesProvidedType;

    /**
     * General notes from overview section
     * @example 'Q&C SOPs & systems are used for all quality activities including disposition, complaint handling, and pharmacovigilance.'
     */
    @Expose()
    generalOverviewNotes: string;

    /**
     * Document control delivery preference
     * @example 'HARD_COPY'
     */
    @Expose()
    documentControlToClient: DocumentControlType;

    /**
     * Document Control Contact ID
     * @example 'a1b2c3d4-e5f6-7890-1234-567890abcdef'
     */
    @Expose()
    documentControlToClientContactId: string;

    /**
     * Product storage configurations selected for this client
     */
    @Expose()
    @Type(() => ProductStorageResponseDto)
    productStorageConfigs?: ProductStorageResponseDto[];

    /**
     * Brand Manager information for the client
     * @example 'N/A'
     */
    @Expose()
    brandManager: string;

    /**
     * Reference number for the Client Quality Manual
     * @example 'QM32'
     */
    @Expose()
    clientQualityManualReferenceNumber: string;

    /**
     * Method of document delivery to client
     * @example 'Hard Copy'
     */
    @Expose()
    documentDeliveryMethod: string;

    /**
     * Contact email for document control purposes
     * @example 'contact@example.com'
     */
    @Expose()
    documentControlContactEmail: string;

    /**
     * SOP training delivered electronically
     * @example true
     */
    @Expose()
    sopTrainingDeliveryElectronically: boolean;

    /**
     * SOP training delivered onsite
     * @example false
     */
    @Expose()
    sopTrainingDeliveryOnsite: boolean;

    /**
     * Who sends/arranges the training
     * @example 'QCO'
     */
    @Expose()
    sopTrainingArrangedBy: string;

    /**
     * SOP training provided to client
     * @example true
     */
    @Expose()
    sopTrainingProvided: boolean;

    /**
     * GMP training provided to client
     * @example false
     */
    @Expose()
    gmpTrainingProvided: boolean;

    /**
     * GMP training delivered electronically
     * @example false
     */
    @Expose()
    gmpTrainingDeliveryElectronically: boolean;

    /**
     * GMP training delivered onsite
     * @example false
     */
    @Expose()
    gmpTrainingDeliveryOnsite: boolean;

    /**
     * Who sends/arranges the GMP training
     * @example 'QCO'
     */
    @Expose()
    gmpTrainingArrangedBy: string;

    /**
     * Timestamp when the record was created
     * @example '2024-01-15T10:30:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    createdAt: Date;

    /**
     * Timestamp when the record was last updated
     * @example '2024-01-15T15:45:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    updatedAt: Date;

    /**
     * General overview items selected for this client
     */
    @Expose()
    @Type(() => GeneralOverviewResponseDto)
    generalOverview?: GeneralOverviewResponseDto[];

    /**
     * Licensable activities selected for this client
     */
    @Expose()
    @Type(() => LicensableActivityResponseDto)
    licensableActivities?: LicensableActivityResponseDto[];

    @Expose()
    @Type(() => RegulatoryDetailsResponseDto)
    regulatoryDetails?: RegulatoryDetailsResponseDto[];

    /**
     * Major version number
     * @example 1
     */
    @Expose()
    versionMajor?: number;

    /**
     * Minor version number
     * @example 0
     */
    @Expose()
    versionMinor?: number;

    /**
     * Version string in format 'major.minor'
     * @example '1.0'
     */
    @Expose()
    versionString?: string;

    /**
     * Timestamp when this version was created
     * @example '2024-01-15T10:30:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    versionCreatedAt?: Date;

    /**
     * User ID who created this version
     * @example 'user-id-123'
     */
    @Expose()
    versionCreatedBy?: string;

    /**
     * Email of the user who last created or edited this record
     * @example 'jane.doe@example.com'
     */
    @Expose()
    lastUpdatedBy?: string | null;
}
